import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, MapPin, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SCHEDULE = {
  monday:    { open: '08:00', close: '22:00', is_closed: false },
  tuesday:   { open: '08:00', close: '22:00', is_closed: false },
  wednesday: { open: '08:00', close: '22:00', is_closed: false },
  thursday:  { open: '08:00', close: '22:00', is_closed: false },
  friday:    { open: '08:00', close: '22:00', is_closed: false },
  saturday:  { open: '08:00', close: '22:00', is_closed: false },
  sunday:    { open: '09:00', close: '22:00', is_closed: false },
};

const DAY_LABELS = {
  monday: 'Luni', tuesday: 'Marți', wednesday: 'Miercuri',
  thursday: 'Joi', friday: 'Vineri', saturday: 'Sâmbătă', sunday: 'Duminică',
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const Locations = () => {
  const { t } = useLanguage();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    phone: '',
    schedule: DEFAULT_SCHEDULE,
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Error loading locations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name,
        address: location.address,
        city: location.city,
        phone: location.phone || '',
        schedule: location.schedule || DEFAULT_SCHEDULE,
      });
    } else {
      setEditingLocation(null);
      setFormData({
        name: '',
        address: '',
        city: '',
        phone: '',
        schedule: DEFAULT_SCHEDULE,
      });
    }
    setDialogOpen(true);
  };

  const updateDaySchedule = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: { ...prev.schedule[day], [field]: value },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await axios.put(`${API}/admin/locations/${editingLocation.id}`, formData);
        toast.success(t('location_updated'));
      } else {
        await axios.post(`${API}/admin/locations`, formData);
        toast.success(t('location_created'));
      }
      setDialogOpen(false);
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.detail || 'Error saving location');
    }
  };

  const handleDelete = async () => {
    if (!locationToDelete) return;
    try {
      await axios.delete(`${API}/admin/locations/${locationToDelete.id}`);
      toast.success(t('location_deleted'));
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.detail || 'Error deleting location');
    }
  };

  const filteredLocations = locations.filter(loc =>
    loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="locations-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{t('locations')}</h1>
          <p className="text-muted-foreground mt-1">
            {locations.length} {t('total_locations').toLowerCase()}
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-location-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('add_location')}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-locations"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredLocations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('location_name')}</TableHead>
                  <TableHead>{t('address')}</TableHead>
                  <TableHead>{t('city')}</TableHead>
                  <TableHead>{t('phone')}</TableHead>
                  <TableHead className="text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLocations.map((location) => (
                  <TableRow key={location.id} data-testid={`location-row-${location.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        {location.name}
                      </div>
                    </TableCell>
                    <TableCell>{location.address}</TableCell>
                    <TableCell>{location.city}</TableCell>
                    <TableCell>{location.phone || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(location)}
                          data-testid={`edit-location-${location.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setLocationToDelete(location);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-location-${location.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <MapPin className="w-12 h-12 mb-4 opacity-50" />
              <p>{t('no_locations')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? t('edit_location') : t('add_location')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('location_name')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="location-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">{t('address')} *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  data-testid="location-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">{t('city')} *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    data-testid="location-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')}</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="location-phone-input"
                  />
                </div>
              </div>
            </div>
              <div className="space-y-2">
                <Label>Program de funcționare</Label>
                <div className="border rounded-md overflow-hidden">
                  {DAY_ORDER.map((day) => {
                    const dayData = formData.schedule[day];
                    return (
                      <div key={day} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 text-sm">
                        <span className="w-20 font-medium shrink-0">{DAY_LABELS[day]}</span>
                        <label className="flex items-center gap-1.5 shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={dayData.is_closed}
                            onChange={(e) => updateDaySchedule(day, 'is_closed', e.target.checked)}
                            className="cursor-pointer"
                          />
                          <span className="text-muted-foreground">Închis</span>
                        </label>
                        <input
                          type="time"
                          value={dayData.open}
                          onChange={(e) => updateDaySchedule(day, 'open', e.target.value)}
                          disabled={dayData.is_closed}
                          className="border rounded px-2 py-1 text-sm bg-background disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                        <span className="text-muted-foreground">—</span>
                        <input
                          type="time"
                          value={dayData.close}
                          onChange={(e) => updateDaySchedule(day, 'close', e.target.value)}
                          disabled={dayData.is_closed}
                          className="border rounded px-2 py-1 text-sm bg-background disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="save-location-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-location-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {locationToDelete?.name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-location">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Locations;
